import { toSnakeCase } from './utils';
/** Aggregations offered by the measure wizard when the calculation is built from the dropdowns. */
export const MEASURE_DRAFT_FUNCTIONS = ['Count', 'Sum', 'Average', 'Min', 'Max'];
/** Number of characters of a SQL expression kept in the drafts list summary. */
const SQL_SUMMARY_MAX_LENGTH = 60;
const getExistingTitles = (dataView) => {
    const titles = new Set();
    const members = [...Object.values(dataView.data?.dimensions || {}), ...Object.values(dataView.data?.measures || {})];
    for (const member of members) {
        const title = member.title?.trim();
        if (title) {
            titles.add(title.toLowerCase());
        }
    }
    return titles;
};
/**
 * Validates the identity of a single measure draft: it needs a name that resolves
 * to a usable internal key and collides neither with an existing dimension/measure
 * nor with another queued draft. Returns '' when the draft is fine, otherwise a
 * single human-readable reason. `editingDraftIndex` excludes the draft that is
 * being re-edited so it does not collide with itself.
 */
export const getMeasureDraftError = (candidate, dataView, drafts, translate, editingDraftIndex) => {
    const name = candidate.name.trim();
    if (!name) {
        return translate('"name" is required and must be a non-empty text value');
    }
    const key = toSnakeCase(name);
    if (!key) {
        return translate('"name" must contain at least one letter or number');
    }
    const normalizedTitle = name.toLowerCase();
    const otherDrafts = drafts.filter((_draft, index) => index !== editingDraftIndex);
    if (getExistingTitles(dataView).has(normalizedTitle)) {
        return translate('A dimension or measure with this name already exists');
    }
    if (otherDrafts.some((draft) => draft.name.trim().toLowerCase() === normalizedTitle)) {
        return translate('Another measure in this list has the same name');
    }
    if (dataView.data?.measures?.[key]) {
        return translate('This name resolves to the same internal identifier ("{key}") as an existing measure', { key });
    }
    if (otherDrafts.some((draft) => toSnakeCase(draft.name) === key)) {
        return translate('Another measure in this list resolves to the same internal identifier ("{key}")', { key });
    }
    return '';
};
/**
 * Final pass over the queued drafts before they are committed. Valid drafts are
 * returned so they can be persisted in a single request, failed ones are returned
 * with their reasons so they stay in the wizard for correction (partial success).
 */
export const validateMeasureDrafts = (drafts, dataView, translate) => {
    const valid = [];
    const failed = [];
    const dimensionKeys = new Set(Object.keys(dataView.data?.dimensions || {}));
    drafts.forEach((draft, index) => {
        const errors = [];
        const name = draft.name.trim();
        // Uniqueness is resolved against the drafts accepted so far, so the first of
        // two colliding drafts is committed and the later one is reported back.
        const nameError = getMeasureDraftError(draft, dataView, valid, translate);
        if (nameError) {
            errors.push(nameError);
        }
        if (draft.calculationType === 'SQL') {
            if (!draft.sql?.trim()) {
                errors.push(translate('"sql" is required and must be a non-empty text value for SQL measures'));
            }
        }
        else {
            if (!MEASURE_DRAFT_FUNCTIONS.includes(draft.measureFunction || '')) {
                errors.push(translate('"measureFunction" must be one of: {functions}', { functions: MEASURE_DRAFT_FUNCTIONS.join(', ') }));
            }
            if (!draft.dimension) {
                errors.push(translate('"dimension" is required for measures that use "measureFunction"'));
            }
            else if (!dimensionKeys.has(draft.dimension)) {
                errors.push(translate('The dimension "{dimension}" does not exist in this dataset', { dimension: draft.dimension }));
            }
        }
        if (errors.length > 0) {
            failed.push({ index, name: name || undefined, errors });
            return;
        }
        valid.push({ ...draft, name });
    });
    return { valid, failed };
};
/** Short description of a draft's calculation, e.g. `Count(subject_id)`, for the drafts list. */
export const getMeasureDraftSummary = (draft) => {
    if (draft.calculationType === 'SQL') {
        const sql = (draft.sql || '').trim().replace(/\s+/g, ' ');
        return sql.length > SQL_SUMMARY_MAX_LENGTH ? `${sql.slice(0, SQL_SUMMARY_MAX_LENGTH)}…` : sql;
    }
    return `${draft.measureFunction}(${draft.dimension})`;
};
